import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { authHeaders } from '../contexts/AuthContext';
import {
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';
import {
  FolderKanban, CheckCircle2, DollarSign, FileText,
  Users, TrendingUp, Loader2, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const AMBER = 'hsl(36,80%,52%)';
const AMBER_LIGHT = 'hsl(36,80%,72%)';
const PIE_COLORS = [AMBER, '#a3a3a3', '#d4d4d4', '#737373', '#e5e5e5'];
const STATUS_COLORS = { active: AMBER, completed: '#a3a3a3', 'on-hold': '#d4d4d4', planning: '#e5e5e5' };

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card hover:border-border transition-all">
      <Icon className="w-4 h-4 text-muted-foreground mb-3" strokeWidth={2} />
      <p className="font-heading text-2xl font-semibold tracking-tight">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-[hsl(36,80%,52%)] mt-1 font-medium">{sub}</p>}
    </motion.div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-heading text-base font-semibold tracking-tight mb-4">{children}</h2>;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-[10px] text-muted-foreground">
          {p.name}: <span className="text-foreground font-medium">{typeof p.value === 'number' && p.value > 1000 ? `₹${(p.value / 100000).toFixed(1)}L` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/analytics`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } catch { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return (
    <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
  );

  if (!data) return (
    <div className="text-center py-24 text-muted-foreground text-sm">Failed to load analytics</div>
  );

  const { summary, project_status, task_breakdown, budget_by_project, monthly_activity, team_breakdown } = data;
  const taskPie = [
    { name: 'Done', value: task_breakdown.done },
    { name: 'In Progress', value: task_breakdown.in_progress },
    { name: 'To Do', value: task_breakdown.todo },
  ].filter(d => d.value > 0);
  const completionRate = summary.total_tasks > 0 ? Math.round((summary.tasks_completed / summary.total_tasks) * 100) : 0;
  const budgetUsed = summary.total_budget > 0 ? Math.round((summary.total_spent / summary.total_budget) * 100) : 0;

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} data-testid="analytics-page">
      <motion.div variants={fadeUp} className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Project metrics, team activity, and budget tracking.</p>
      </motion.div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FolderKanban} label="Total Projects" value={summary.total_projects} sub={`${summary.active_projects} active`} />
        <StatCard icon={CheckCircle2} label="Task Completion" value={`${completionRate}%`} sub={`${summary.tasks_completed}/${summary.total_tasks} done`} />
        <StatCard icon={DollarSign} label="Total Budget" value={summary.total_budget > 0 ? `₹${(summary.total_budget / 100000).toFixed(1)}L` : '—'} sub={summary.total_budget > 0 ? `${budgetUsed}% utilized` : undefined} />
        <StatCard icon={Users} label="Network Size" value={summary.network_size} sub={`${summary.total_files} files`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Budget by Project */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card" data-testid="budget-chart">
          <SectionTitle>Budget by Project</SectionTitle>
          {budget_by_project.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={budget_by_project} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(0,0%,90%)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={v => v > 1000 ? `${(v/100000).toFixed(0)}L` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="budget" name="Budget" fill="#e5e5e5" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spent" name="Spent" fill={AMBER} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-12">No projects yet</p>
          )}
        </motion.div>

        {/* Monthly Activity */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card" data-testid="activity-chart">
          <SectionTitle>Monthly Activity</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={monthly_activity} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(0,0%,90%)" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="tasks" name="Tasks" stroke={AMBER} fill={AMBER} fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="files" name="Files" stroke="#a3a3a3" fill="#a3a3a3" fillOpacity={0.08} strokeWidth={1.5} />
              <Area type="monotone" dataKey="team" name="Team Adds" stroke="#d4d4d4" fill="#d4d4d4" fillOpacity={0.05} strokeWidth={1} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Task Breakdown */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card" data-testid="task-breakdown">
          <SectionTitle>Task Breakdown</SectionTitle>
          {taskPie.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={taskPie} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {taskPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {taskPie.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }} />
                    <span className="text-xs text-muted-foreground">{d.name}</span>
                    <span className="text-xs font-medium ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No tasks yet</p>
          )}
        </motion.div>

        {/* Project Status */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card" data-testid="project-status-chart">
          <SectionTitle>Project Status</SectionTitle>
          {project_status.length > 0 ? (
            <div className="space-y-3">
              {project_status.map(({ status, count }) => (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs capitalize text-muted-foreground">{status}</span>
                    <span className="text-xs font-medium">{count}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-secondary">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(count / Math.max(summary.total_projects, 1)) * 100}%`, backgroundColor: STATUS_COLORS[status] || '#d4d4d4' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No projects yet</p>
          )}
        </motion.div>

        {/* Team by Role */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card" data-testid="team-breakdown">
          <SectionTitle>Team by Role</SectionTitle>
          {team_breakdown.length > 0 ? (
            <div className="space-y-2.5">
              {team_breakdown.map(({ role, count }) => (
                <div key={role} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/40">
                  <span className="text-xs font-medium">{role}</span>
                  <Badge variant="secondary" className="text-[10px] h-5 px-2">{count}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No team data yet</p>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
